import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Switch, Input } from '@you/ui'
import { Search, Plus } from 'lucide-react'
import { formatDateTime } from '@/lib/utils'
import { useOrganization } from '@/api/hooks/useOrganization'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import type { Organization } from '@/api/types/organization.type'
import { useOrganizationStore } from '@/stores/organization'
import UpdateDialog from '@/components/Organization/UpdateDialog'

const OrganizationList = () => {
  const navigate = useNavigate()
  const [searchKeyword, setSearchKeyword] = useState('')
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null)
  const [deletingOrgId, setDeletingOrgId] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { currentWorkspace } = useWorkspaceStore()
  const { setCurrentOrganization } = useOrganizationStore()

  // 获取组织列表
  const {
    organizationList,
    isLoading,
    saveOrUpdateOrg,
    removeOrg,
    toggleOrgStatus,
  } = useOrganization({
    workspaceId: currentWorkspace?.id || '',
    search: searchKeyword,
  })

  // 根据搜索关键词过滤（如果API不支持搜索，则在前端过滤）
  const filteredOrganizations = organizationList.filter((org: Organization) =>
    org.name.toLowerCase().includes(searchKeyword.toLowerCase())
  )

  /**
   * 处理搜索
   */
  const handleSearch = (value: string) => {
    setSearchKeyword(value)
  }

  /**
   * 处理新建
   */
  const handleCreate = () => {
    setEditingOrg(null)
    setIsDialogOpen(true)
  }

  /**
   * 处理编辑
   */
  const handleEdit = (organization: Organization) => {
    setEditingOrg(organization)
    setIsDialogOpen(true)
  }

  /**
   * 处理删除
   */
  const handleDelete = async (id: string) => {
    setDeletingOrgId(id)
    setIsDeleteDialogOpen(true)
  }

  /**
   * 确认删除
   */
  const confirmDelete = async () => {
    try {
      setIsSubmitting(true)
      await removeOrg(deletingOrgId)
      setIsDeleteDialogOpen(false)
      setDeletingOrgId('')
    } catch (error) {
      console.error('删除组织失败:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  /**
   /**
    * 提交表单
    */
  const handleSubmit = async ({
    id,
    name,
    workspaceId,
  }: {
    id?: string
    name: string
    workspaceId?: string
  }) => {
    setIsSubmitting(true)
    try {
      await saveOrUpdateOrg({ id, name, workspaceId: workspaceId || '' })
      setIsDialogOpen(false)
      handleCloseDialog()
    } finally {
      setIsSubmitting(false)
    }
  }

  /**
   * 关闭对话框
   */
  const handleCloseDialog = () => {
    setIsDialogOpen(false)
    setEditingOrg(null)
  }

  /**
   * 处理状态切换
   */
  const handleStatusToggle = async (id: string, checked: boolean) => {
    try {
      await toggleOrgStatus(id, checked ? 1 : 0)
      // 状态切换成功后的处理
    } catch (error) {
      console.error('切换组织状态失败:', error)
    }
  }

  /**
   * 获取状态显示信息
   */
  const getStatusInfo = (isEnabled: boolean) => {
    return {
      label: isEnabled ? '已启用' : '已停用',
    }
  }

  /**
   * 处理组织名称点击
   */
  const handleOrganizationClick = (organization: Organization) => {
    setCurrentOrganization(organization)
    navigate(`/organization/details?id=${organization.id}`)
  }

  return (
    <div className="h-full flex flex-col space-y-2">
      {/* 标题部分 */}
      <div className="flex-shrink-0">
        <h1 className="text-xl font-semibold text-gray-900">组织结构</h1>
      </div>

      {/* 操作栏 */}
      <div className="flex-shrink-0 flex justify-between items-center">
        <div className="flex-1" />
        <div className="flex items-center space-x-4">
          {/* 搜索框 */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="搜索组织..."
              value={searchKeyword}
              onChange={(e) => handleSearch(e.target.value)}
              className="px-0 w-[300px] pl-9 pr-2"
            />
          </div>

          {/* 新建按钮 */}
          <Button
            onClick={handleCreate}
            className="flex items-center space-x-2 bg-gradient-gentle"
          >
            <Plus className="w-4 h-4" />
            <span>新建</span>
          </Button>
        </div>
      </div>

      {/* 表格部分 */}
      <div className="flex-1 flex flex-col bg-white border border-[#F7F9FF] rounded-lg overflow-hidden">
        {/* 表头 - 固定 */}
        <div
          className="flex-shrink-0 border-b"
          style={{ borderColor: '#F7F9FF' }}
        >
          <table className="w-full table-fixed">
            <thead className="[&>tr>th]:text-center [&>tr>th]:text-xs [&>tr>th]:font-medium [&>tr>th]:uppercase [&>tr>th]:tracking-wider [&>tr>th]:px-6 [&>tr>th]:py-3">
              <tr style={{ backgroundColor: '#EFF3FD', height: '40px' }}>
                <th className="w-1/3" style={{ color: '#333333' }}>
                  组织名称
                </th>
                <th className="w-1/6" style={{ color: '#333333' }}>
                  状态
                </th>
                <th className="w-1/4" style={{ color: '#333333' }}>
                  更新时间
                </th>
                <th className="w-1/4" style={{ color: '#333333' }}>
                  操作
                </th>
              </tr>
            </thead>
          </table>
        </div>

        {/* 表格内容 - 可滚动 */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">加载中...</div>
            </div>
          ) : filteredOrganizations.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">
                {searchKeyword ? '未找到匹配的组织' : '暂无组织数据'}
              </div>
            </div>
          ) : (
            <table className="w-full table-fixed">
              <tbody className="divide-y" style={{ borderColor: '#F7F9FF' }}>
                {filteredOrganizations.map((org: Organization) => (
                  <tr
                    key={org.id}
                    className="hover:bg-[#F9FAFF] [&>td]:text-center [&>td]:text-base [&>td]:px-6 [&>td]:py-4"
                    style={{ height: '80px', borderColor: '#F7F9FF' }}
                  >
                    <td className="w-1/3" style={{ color: '#8047FF' }}>
                      <button
                        onClick={() => handleOrganizationClick(org)}
                        className="hover:underline cursor-pointer bg-transparent border-none p-0"
                      >
                        {org.name}
                      </button>
                    </td>
                    <td className="w-1/6">
                      <div className="flex items-center justify-center space-x-1">
                        <Switch
                          checked={org.status === 1}
                          onCheckedChange={(checked) =>
                            handleStatusToggle(org.id, checked)
                          }
                        />
                        {(() => {
                          const statusInfo = getStatusInfo(org.status === 1)
                          return (
                            <span className="text-xs">{statusInfo.label}</span>
                          )
                        })()}
                      </div>
                    </td>
                    <td className="text-gray-500 w-1/4">
                      {formatDateTime(org.updateTime)}
                    </td>
                    <td className="space-x-2 w-1/4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(org)}
                      >
                        编辑
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(org.id)}
                      >
                        删除
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* 新建/编辑对话框 */}
      <UpdateDialog
        open={isDialogOpen}
        onOpenChange={(o) => {
          setIsDialogOpen(o)
          if (!o) handleCloseDialog()
        }}
        entity="organization"
        id={editingOrg?.id}
        initialName={editingOrg?.name}
        workspaceId={currentWorkspace?.id}
        loading={isSubmitting}
        onSubmit={handleSubmit}
      />

      {/* 简化删除确认，保持现有交互 */}
      {isDeleteDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg p-6 w-[420px] shadow-xl">
            <div className="text-lg font-medium mb-3">删除确认</div>
            <div className="text-sm text-gray-600 mb-6">
              确定要删除这个组织吗？此操作不可恢复。
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setIsDeleteDialogOpen(false)}
                disabled={isSubmitting}
              >
                取消
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDelete}
                disabled={isSubmitting}
              >
                {isSubmitting ? '删除中...' : '确定删除'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default OrganizationList
